/**
 * Permissões granulares — ADR-039 (5 camadas) + ADR-056 (text[] + GIN).
 *
 * Esta fatia (B-bis) refatora `BondPermissions` de objeto-com-8-chaves
 * (Fatia B original) para **string[] catalogado** conforme ADR-056:
 * performance hot-path (30k-500k queries/dia em tenants médios) exige
 * `text[]` + index GIN nativo do PostgreSQL.
 *
 * Camadas 1, 2 e 3 implementadas. Camadas 4 (faction) e 5 (bubble)
 * permanecem como hooks no-op no PermissionCalculator até que a Fatia C
 * (Adversarial + Bubbles) entre.
 *
 * Operação entre camadas: interseção (mais restritiva prevalece), com
 * exceção declarada da Camada 3 (Bond) que pode **elevar** permissão
 * quando ACL específica do Bond concede explicitamente.
 */
/**
 * Catálogo de chaves de permissão. Catálogo gerenciado via Capability
 * Registry class='bond-permission-compatibility' (ADR-082, ADR-059):
 * cada bond.type declara quais chaves são válidas.
 *
 * 8 chaves canônicas + chaves específicas de bond.type (financial,
 * medical-decisions, court-filing, etc.) que ficam fora deste catálogo
 * stricto — vivem no Registry.
 */
export const BOND_PERMISSION_KEYS = [
    'read',
    'read-redacted',
    'write',
    'share',
    'delete',
    'admin',
    'export',
    'audit-view',
];
export const PERMISSION_SCOPES = [
    'full',
    'messages-only',
    'metadata-only',
    'specific-fields',
];
/**
 * Cross-reference entre Bonds — ADR-042.
 */
export const BOND_CROSS_REF_RELATIONSHIPS = [
    'competes-with',
    'succeeds',
    'references',
    'derived-from',
    'related-to',
    'merged-with',
];
export const BOND_CROSS_REF_DIRECTIONS = ['incoming', 'outgoing', 'bidirectional'];
/** Permissions vazias (sem nenhuma chave) — útil como default/fallback. */
export const NO_PERMISSIONS = Object.freeze([]);
/** Conjunto canônico de todas as 8 chaves base — útil para admin_tenant. */
export const ALL_PERMISSIONS = Object.freeze([
    'read',
    'read-redacted',
    'write',
    'share',
    'delete',
    'admin',
    'export',
    'audit-view',
]);
/**
 * Interseção de dois conjuntos de permissões (operação default entre
 * camadas — mais restritivo prevalece).
 */
export function intersectPermissions(a, b) {
    const setB = new Set(b);
    return Object.freeze(a.filter((key) => setB.has(key)));
}
/**
 * União de dois conjuntos de permissões (operação para Camada 3 — Bond
 * pode elevar permissão via ACL explícita).
 */
export function unionPermissions(a, b) {
    const merged = new Set([...a, ...b]);
    return Object.freeze([...merged]);
}
/** Verifica se conjunto contém todas as permissões requeridas. */
export function hasAllPermissions(granted, required) {
    const set = new Set(granted);
    return required.every((key) => set.has(key));
}
/** Retorna permissões requeridas que faltam em `granted`. */
export function missingPermissions(granted, required) {
    const set = new Set(granted);
    return required.filter((key) => !set.has(key));
}
//# sourceMappingURL=permissions.js.map